import React from 'react';
import { api } from '../../api';
import { colors as themeColors, fontFamily as themeFontFamily, optionStyle, selectSurface } from '../../theme';
import ToastHost, { useToast } from './Toast';

export default function MessagesView({ user, onUpdateUnread }) {
  const { toast, showToast } = useToast();
  const [messages, setMessages] = React.useState([]);
  const [selectedMessage, setSelectedMessage] = React.useState(null);
  const [messageView, setMessageView] = React.useState('inbox');
  const [msgFilter, setMsgFilter] = React.useState('all'); // 'all' | 'open' | 'resolved'
  const [replyText, setReplyText] = React.useState('');
  const [showBroadcast, setShowBroadcast] = React.useState(false);
  const [showNewMessage, setShowNewMessage] = React.useState(false);
  const [showContactCS, setShowContactCS] = React.useState(false);
  const [csSubject, setCsSubject] = React.useState('');
  const [csMessage, setCsMessage] = React.useState('');
  const [broadcastSubject, setBroadcastSubject] = React.useState('');
  const [broadcastMessage, setBroadcastMessage] = React.useState('');
  const [accounts, setAccounts] = React.useState([]);
  const [myBook, setMyBook] = React.useState(null); // for agents
  const [selectedRecipient, setSelectedRecipient] = React.useState(null);
  const [messageSubject, setMessageSubject] = React.useState('');
  const [messageText, setMessageText] = React.useState('');

  React.useEffect(() => {
    loadMessages();
    loadAccounts();
    if (user.role === 'agent') {
      api.getMyBook().then(d => setMyBook(d.book || null)).catch(() => {});
    }
  }, [messageView]);

  const loadAccounts = async () => {
    try {
      const data = await api.getAccounts();
      setAccounts(data.users || []);
    } catch (err) {
      console.error('Failed to load accounts:', err);
    }
  };

  const loadMessages = async () => {
    try {
      const data = messageView === 'inbox'
        ? await api.getInbox()
        : await api.getSentMessages();
      setMessages(data.messages || []);
    } catch (err) {
      console.error('Failed to load messages:', err);
    }
  };

  const openMessage = async (message) => {
    try {
      const data = await api.getMessage(message.id);
      setSelectedMessage(data);
      if (!message.is_read && messageView === 'inbox') {
        await api.markMessageRead(message.id);
        await loadMessages();
        onUpdateUnread();
      }
    } catch (err) {
      console.error('Failed to open message:', err);
    }
  };

  const replyToMessage = async () => {
    if (!replyText.trim()) return;

    try {
      await api.replyToMessage(selectedMessage.message.id, replyText);
      showToast('Reply sent');
      setReplyText('');
      await openMessage({ id: selectedMessage.message.id });
      await loadMessages();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to send reply', 'error');
    }
  };

  const resolveMessage = async (messageId) => {
    try {
      await api.resolveMessage(messageId);
      await openMessage({ id: messageId });
      await loadMessages();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to resolve message', 'error');
    }
  };

  const unresolveMessage = async (messageId) => {
    try {
      await api.unresolveMessage(messageId);
      await openMessage({ id: messageId });
      await loadMessages();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to unresolve message', 'error');
    }
  };

  const sendBroadcast = async () => {
    if (!broadcastSubject.trim() || !broadcastMessage.trim()) {
      showToast('Please fill in both subject and message', 'error');
      return;
    }

    try {
      for (const targetUser of accounts) {
        await api.sendMessage(broadcastSubject, broadcastMessage, targetUser.id);
      }

      showToast(`Broadcast sent to ${accounts.length} users`);
      setShowBroadcast(false);
      setBroadcastSubject('');
      setBroadcastMessage('');
      await loadMessages();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to send broadcast', 'error');
    }
  };

  const sendIndividualMessage = async () => {
    if (!selectedRecipient || !messageSubject.trim() || !messageText.trim()) {
      showToast('Please select recipient and fill in all fields', 'error');
      return;
    }

    try {
      await api.sendMessage(messageSubject, messageText, selectedRecipient);
      showToast('Message sent');
      setShowNewMessage(false);
      setSelectedRecipient(null);
      setMessageSubject('');
      setMessageText('');
      await loadMessages();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to send message', 'error');
    }
  };

  const sendToCS = async () => {
    if (!csSubject.trim() || !csMessage.trim()) {
      showToast('Please fill in both subject and message', 'error');
      return;
    }
    try {
      await api.sendMessage(csSubject, csMessage, null, true);
      showToast('Message sent to Customer Service');
      setShowContactCS(false);
      setCsSubject('');
      setCsMessage('');
      await loadMessages();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to send message', 'error');
    }
  };

  if (selectedMessage) {
    return (
      <>
      <ToastHost toast={toast} />
      <div className="rounded-lg shadow p-3 md:p-6" style={{ background: themeColors.cardBg, boxShadow: themeColors.shadowCard }}>
        <button
          onClick={() => setSelectedMessage(null)}
          className="mb-3 md:mb-4 flex items-center gap-1 md:gap-2 text-sm md:text-base font-bold"
          style={{ color: themeColors.textPrimary, fontFamily: themeFontFamily }}
        >
          ← Back to {messageView === 'inbox' ? 'Inbox' : 'Sent'}
        </button>

        <div className="pb-3 md:pb-4 mb-3 md:mb-4" style={{ borderBottom: '1px solid rgba(191,149,63,0.3)' }}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-2">
            <h2 className="text-lg md:text-2xl font-bold" style={{ color: themeColors.textPrimary, fontFamily: themeFontFamily }}>{selectedMessage.message.subject}</h2>
            <div className="flex gap-2">
              {messageView === 'inbox' && (
                selectedMessage.message.is_resolved ? (
                  <button
                    onClick={() => unresolveMessage(selectedMessage.message.id)}
                    className="px-2 md:px-3 py-1 rounded text-xs md:text-sm font-bold"
                    style={{ background: 'rgba(108,117,125,0.3)', color: '#adb5bd', border: '1px solid rgba(108,117,125,0.5)', fontFamily: themeFontFamily }}
                  >
                    Reopen
                  </button>
                ) : (
                  <button
                    onClick={() => resolveMessage(selectedMessage.message.id)}
                    className="px-2 md:px-3 py-1 rounded text-xs md:text-sm font-bold"
                    style={{ background: themeColors.cardBgActive, color: themeColors.textPrimary, fontFamily: themeFontFamily }}
                  >
                    Mark Resolved
                  </button>
                )
              )}
            </div>
          </div>
          <div className="text-xs md:text-sm" style={{ color: themeColors.textPrimary }}>
            <p>From: {selectedMessage.message.sender_name}</p>
            <p>To: {selectedMessage.message.recipient_name}</p>
            <p>Date: {new Date(selectedMessage.message.created_at).toLocaleString()}</p>
          </div>
        </div>

        <div className="rounded p-3 md:p-4 mb-3 md:mb-4" style={{ background: themeColors.cardBg }}>
          <p className="whitespace-pre-wrap text-sm md:text-base" style={{ color: themeColors.textPrimary }}>{selectedMessage.message.message}</p>
        </div>

        {selectedMessage.replies && selectedMessage.replies.length > 0 && (
          <div className="space-y-3 md:space-y-4 mb-3 md:mb-4">
            <h3 className="font-semibold text-sm md:text-base" style={{ color: themeColors.textPrimary, fontFamily: themeFontFamily }}>Replies:</h3>
            {selectedMessage.replies.map((reply) => (
              <div key={reply.id} className="rounded p-3 md:p-4 ml-2 md:ml-8" style={{ background: themeColors.cardBg, border: `1px solid ${themeColors.goldMuted}` }}>
                <div className="text-xs md:text-sm mb-2" style={{ color: themeColors.gold }}>
                  <strong>{reply.sender_name}</strong> - {new Date(reply.created_at).toLocaleString()}
                </div>
                <p className="whitespace-pre-wrap text-sm md:text-base" style={{ color: themeColors.textPrimary }}>{reply.message}</p>
              </div>
            ))}
          </div>
        )}

        {messageView === 'inbox' && (
          <div className="pt-3 md:pt-4" style={{ borderTop: '1px solid rgba(191,149,63,0.3)' }}>
            <h3 className="font-semibold mb-2 text-sm md:text-base" style={{ color: themeColors.textPrimary, fontFamily: themeFontFamily }}>Reply:</h3>
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Type your reply..."
              className="w-full rounded p-2 md:p-3 mb-2 text-sm md:text-base"
              style={{ background: themeColors.cardBg, color: themeColors.textPrimary, border: `1px solid ${themeColors.goldMuted}`, fontFamily: themeFontFamily }}
              rows="4"
            />
            <button
              onClick={replyToMessage}
              className="px-3 md:px-4 py-2 rounded text-sm md:text-base font-bold"
              style={{ background: themeColors.cardBgActive, color: themeColors.textPrimary, fontFamily: themeFontFamily }}
            >
              Send Reply
            </button>
          </div>
        )}
      </div>
      </>
    );
  }

  return (
    <>
    <ToastHost toast={toast} />
    <div className="rounded-lg shadow" style={{ background: themeColors.cardBg, boxShadow: themeColors.shadowCard }}>
      <div className="p-3 md:p-4" style={{ borderBottom: '1px solid rgba(191,149,63,0.3)' }}>
        {/* Mobile Layout */}
        <div className="md:hidden space-y-2">
          <div className="flex gap-2">
            <button
              onClick={() => setMessageView('inbox')}
              className={`flex-1 px-3 py-2 rounded text-sm font-bold`}
              style={messageView === 'inbox' ? { background: themeColors.cardBgActive, color: themeColors.textPrimary, fontFamily: themeFontFamily } : { background: themeColors.sectionBg, color: themeColors.textPrimary, fontFamily: themeFontFamily }}
            >
              Inbox
            </button>
            <button
              onClick={() => setMessageView('sent')}
              className={`flex-1 px-3 py-2 rounded text-sm font-bold`}
              style={messageView === 'sent' ? { background: themeColors.cardBgActive, color: themeColors.textPrimary, fontFamily: themeFontFamily } : { background: themeColors.sectionBg, color: themeColors.textPrimary, fontFamily: themeFontFamily }}
            >
              Sent
            </button>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowNewMessage(true)}
              className="px-3 py-1.5 rounded text-xs font-bold"
              style={{ background: themeColors.cardBgActive, color: themeColors.textPrimary, fontFamily: themeFontFamily }}
            >
              New
            </button>
            <button
              onClick={() => setShowBroadcast(true)}
              className="px-3 py-1.5 rounded text-xs font-bold"
              style={{ background: themeColors.goldActiveBg, color: themeColors.textPrimary, border: `1px solid ${themeColors.gold}`, fontFamily: themeFontFamily }}
            >
              Broadcast
            </button>
            {user.role === 'book' && (
              <button
                onClick={() => setShowContactCS(true)}
                className="px-3 py-1.5 rounded text-xs font-bold"
                style={{ background: themeColors.sectionBg, color: themeColors.textPrimary, border: `1px solid ${themeColors.gold}`, fontFamily: themeFontFamily }}
              >
                Contact Support
              </button>
            )}
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:flex justify-between items-center">
          <div className="flex gap-2">
            <button
              onClick={() => setMessageView('inbox')}
              className={`px-4 py-2 rounded font-bold`}
              style={messageView === 'inbox' ? { background: themeColors.cardBgActive, color: themeColors.textPrimary, fontFamily: themeFontFamily } : { background: themeColors.sectionBg, color: themeColors.textPrimary, fontFamily: themeFontFamily }}
            >
              Inbox
            </button>
            <button
              onClick={() => setMessageView('sent')}
              className={`px-4 py-2 rounded font-bold`}
              style={messageView === 'sent' ? { background: themeColors.cardBgActive, color: themeColors.textPrimary, fontFamily: themeFontFamily } : { background: themeColors.sectionBg, color: themeColors.textPrimary, fontFamily: themeFontFamily }}
            >
              Sent
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowNewMessage(true)}
              className="px-4 py-2 rounded flex items-center gap-2 font-bold"
              style={{ background: themeColors.cardBgActive, color: themeColors.textPrimary, fontFamily: themeFontFamily }}
            >
              New Message
            </button>
            <button
              onClick={() => setShowBroadcast(true)}
              className="px-4 py-2 rounded flex items-center gap-2 font-bold"
              style={{ background: themeColors.goldActiveBg, color: themeColors.textPrimary, border: `1px solid ${themeColors.gold}`, fontFamily: themeFontFamily }}
            >
              Broadcast
            </button>
            {user.role === 'book' && (
              <button
                onClick={() => setShowContactCS(true)}
                className="px-4 py-2 rounded flex items-center gap-2 font-bold"
                style={{ background: themeColors.sectionBg, color: themeColors.textPrimary, border: `1px solid ${themeColors.gold}`, fontFamily: themeFontFamily }}
              >
                Contact Support
              </button>
            )}
          </div>
        </div>
      </div>

      {messageView === 'inbox' && user.role === 'book' && (
        <div style={{ padding: '0.5rem 1rem', borderBottom: '1px solid rgba(191,149,63,0.15)', display: 'flex', gap: '0.4rem' }}>
          {['all', 'open', 'resolved'].map(f => (
            <button key={f} onClick={() => setMsgFilter(f)} style={{ padding: '0.25rem 0.75rem', borderRadius: 20, fontSize: '0.78rem', fontFamily: themeFontFamily, cursor: 'pointer', border: msgFilter === f ? `1px solid ${themeColors.gold}` : `1px solid ${themeColors.goldMuted}`, background: msgFilter === f ? themeColors.goldActiveBg : 'transparent', color: msgFilter === f ? themeColors.textPrimary : themeColors.textMuted, textTransform: 'capitalize' }}>
              {f}
            </button>
          ))}
        </div>
      )}

      <div className="divide-y" style={{ borderColor: themeColors.goldMuted }}>
        {(() => {
          const filtered = messageView !== 'inbox' || msgFilter === 'all' ? messages
            : msgFilter === 'open' ? messages.filter(m => !m.is_resolved)
            : messages.filter(m => m.is_resolved);
          return filtered.length === 0 ? (
          <div className="p-6 md:p-8 text-center text-sm md:text-base" style={{ color: themeColors.textPrimary }}>
            No messages yet
          </div>
        ) : (
          filtered.map((message) => (
            <div
              key={message.id}
              onClick={() => openMessage(message)}
              className="p-3 md:p-4 cursor-pointer"
              style={{ background: !message.is_read && messageView === 'inbox' ? themeColors.goldActiveBg : 'transparent', borderBottom: '1px solid rgba(191,149,63,0.15)' }}
            >
              <div className="flex justify-between items-start mb-1">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 md:gap-2 flex-wrap">
                    {!message.is_read && messageView === 'inbox' && (
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: themeColors.gold }}></span>
                    )}
                    <h3 className="font-semibold text-sm md:text-base truncate" style={{ color: themeColors.textPrimary, fontFamily: themeFontFamily }}>{message.subject}</h3>
                    {message.is_resolved && (
                      <span className="text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded" style={{ background: '#1a3a1a', color: '#4ade80' }}>
                        Resolved
                      </span>
                    )}
                  </div>
                  <p className="text-xs md:text-sm truncate" style={{ color: themeColors.gold }}>
                    {messageView === 'inbox'
                      ? `From: ${message.sender_name}`
                      : `To: ${message.recipient_name}`}
                  </p>
                </div>
                <div className="text-[10px] md:text-xs ml-2 flex-shrink-0" style={{ color: themeColors.textPrimary }}>
                  {new Date(message.created_at).toLocaleDateString()}
                </div>
              </div>
              <p className="text-xs md:text-sm line-clamp-2" style={{ color: themeColors.textPrimary }}>{message.message}</p>
              {message.reply_count > 0 && (
                <p className="text-[10px] md:text-xs mt-1" style={{ color: themeColors.gold }}>
                  {message.reply_count} {message.reply_count === 1 ? 'reply' : 'replies'}
                </p>
              )}
            </div>
          ))
        );
        })()}
      </div>

      {showNewMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="rounded-lg max-w-2xl w-full p-6" style={{ background: themeColors.bgGradient, border: `1px solid ${themeColors.goldMuted}` }}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold" style={{ color: themeColors.textPrimary, fontFamily: themeFontFamily }}>Send Message to User</h2>
              <button onClick={() => setShowNewMessage(false)} className="text-2xl" style={{ color: themeColors.textPrimary }}>
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: themeColors.textPrimary, fontFamily: themeFontFamily }}>Recipient</label>
                <select
                  value={selectedRecipient || ''}
                  onChange={(e) => setSelectedRecipient(Number(e.target.value))}
                  className="w-full rounded px-3 py-2"
                  style={{ background: selectSurface, color: themeColors.textPrimary, border: `1px solid ${themeColors.goldMuted}`, fontFamily: themeFontFamily, fontWeight: 'bold' }}
                >
                  <option style={optionStyle} value="">Select a user...</option>
                  {user.role === 'agent' && myBook && (
                    <option style={optionStyle} value={myBook.id}>{myBook.name} - Book</option>
                  )}
                  {accounts.map((account) => (
                    <option style={optionStyle} key={account.id} value={account.id}>
                      {account.name} ({account.username}) - {account.role.charAt(0).toUpperCase() + account.role.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: themeColors.textPrimary, fontFamily: themeFontFamily }}>Subject</label>
                <input
                  type="text"
                  value={messageSubject}
                  onChange={(e) => setMessageSubject(e.target.value)}
                  placeholder="Message subject..."
                  className="w-full rounded px-3 py-2"
                  style={{ background: themeColors.cardBg, color: themeColors.textPrimary, border: `1px solid ${themeColors.goldMuted}`, fontFamily: themeFontFamily, fontWeight: 'bold' }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: themeColors.textPrimary }}>Message</label>
                <textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="Your message..."
                  className="w-full rounded px-3 py-2"
                  style={{ background: themeColors.cardBg, color: themeColors.textPrimary, border: `1px solid ${themeColors.goldMuted}`, fontFamily: themeFontFamily }}
                  rows="6"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={sendIndividualMessage}
                  className="flex-1 py-2 rounded font-bold"
                  style={{ background: themeColors.cardBgActive, color: themeColors.textPrimary, fontFamily: themeFontFamily }}
                >
                  Send Message
                </button>
                <button
                  onClick={() => setShowNewMessage(false)}
                  className="px-4 py-2 rounded font-bold"
                  style={{ background: themeColors.sectionBg, color: themeColors.textPrimary, border: `1px solid ${themeColors.goldMuted}`, fontFamily: themeFontFamily }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Customer Service Modal */}
      {showContactCS && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="rounded-lg max-w-2xl w-full p-6" style={{ background: themeColors.bgGradient, border: `1px solid ${themeColors.goldMuted}` }}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold" style={{ color: themeColors.textPrimary, fontFamily: themeFontFamily }}>Contact Customer Service</h2>
              <button onClick={() => setShowContactCS(false)} className="text-2xl" style={{ color: themeColors.textPrimary }}>
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div className="rounded p-3 text-sm" style={{ background: themeColors.cardBg, border: `1px solid ${themeColors.goldMuted}`, color: themeColors.textPrimary }}>
                This message will be sent directly to customer support.
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: themeColors.textPrimary, fontFamily: themeFontFamily }}>Subject</label>
                <input
                  type="text"
                  value={csSubject}
                  onChange={(e) => setCsSubject(e.target.value)}
                  placeholder="Message subject..."
                  className="w-full rounded px-3 py-2"
                  style={{ background: themeColors.cardBg, color: themeColors.textPrimary, border: `1px solid ${themeColors.goldMuted}`, fontFamily: themeFontFamily, fontWeight: 'bold' }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: themeColors.textPrimary, fontFamily: themeFontFamily }}>Message</label>
                <textarea
                  value={csMessage}
                  onChange={(e) => setCsMessage(e.target.value)}
                  placeholder="Describe the issue..."
                  className="w-full rounded px-3 py-2"
                  style={{ background: themeColors.cardBg, color: themeColors.textPrimary, border: `1px solid ${themeColors.goldMuted}`, fontFamily: themeFontFamily }}
                  rows="6"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={sendToCS}
                  className="flex-1 py-2 rounded font-bold"
                  style={{ background: themeColors.cardBgActive, color: themeColors.textPrimary, fontFamily: themeFontFamily }}
                >
                  Send to Customer Service
                </button>
                <button
                  onClick={() => setShowContactCS(false)}
                  className="px-4 py-2 rounded font-bold"
                  style={{ background: themeColors.sectionBg, color: themeColors.textPrimary, border: `1px solid ${themeColors.goldMuted}`, fontFamily: themeFontFamily }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Broadcast Modal */}
      {showBroadcast && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="rounded-lg max-w-2xl w-full p-6" style={{ background: themeColors.bgGradient, border: `1px solid ${themeColors.goldMuted}` }}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold" style={{ color: themeColors.textPrimary, fontFamily: themeFontFamily }}>Send Broadcast Message</h2>
              <button onClick={() => setShowBroadcast(false)} className="text-2xl" style={{ color: themeColors.textPrimary }}>
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: themeColors.textPrimary, fontFamily: themeFontFamily }}>Subject</label>
                <input
                  type="text"
                  value={broadcastSubject}
                  onChange={(e) => setBroadcastSubject(e.target.value)}
                  placeholder="Announcement subject..."
                  className="w-full rounded px-3 py-2"
                  style={{ background: themeColors.cardBg, color: themeColors.textPrimary, border: `1px solid ${themeColors.goldMuted}`, fontFamily: themeFontFamily, fontWeight: 'bold' }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: themeColors.textPrimary, fontFamily: themeFontFamily }}>Message</label>
                <textarea
                  value={broadcastMessage}
                  onChange={(e) => setBroadcastMessage(e.target.value)}
                  placeholder="Your announcement..."
                  className="w-full rounded px-3 py-2"
                  style={{ background: themeColors.cardBg, color: themeColors.textPrimary, border: `1px solid ${themeColors.goldMuted}`, fontFamily: themeFontFamily }}
                  rows="6"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={sendBroadcast}
                  className="flex-1 py-2 rounded font-bold"
                  style={{ background: themeColors.goldActiveBg, color: themeColors.textPrimary, border: `1px solid ${themeColors.gold}`, fontFamily: themeFontFamily }}
                >
                  Send to All {accounts.length} Users
                </button>
                <button
                  onClick={() => setShowBroadcast(false)}
                  className="px-4 py-2 rounded font-bold"
                  style={{ background: themeColors.sectionBg, color: themeColors.textPrimary, border: `1px solid ${themeColors.goldMuted}`, fontFamily: themeFontFamily }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
